import React, { useState, useEffect } from "react";
import { IResourceComponentsProps } from "@pankod/refine-core";
import { List, Card, Button, Space, Select, Typography, Table, Tag, DatePicker, Modal, Input } from "@pankod/refine-antd";
import toast from "react-hot-toast";
import { fetchConfiguration, updateConfiguration, refreshFromPronto, bulkUpdatePowerboardStartDate, findDuplicateDrafts, cleanupDuplicateDrafts, getStuckSubscriptionOrders, getOpenSubscriptionRun, movePendingOrderToRun } from "Services/productServices";
import { PRODUCT_TYPES } from "../../constants";
import { format } from "date-fns";
import dayjs from "dayjs";

const { Option } = Select;
const { Text, Title } = Typography;

export const AdminTools: React.FC<IResourceComponentsProps & { openRouterSettings?: React.ReactNode }> = ({ openRouterSettings }) => {
  const [disabledAttributeCodes, setDisabledAttributeCodes] = useState<string[]>([]);
  const [activeWarehouseCode, setActiveWarehouseCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [prontoIdsText, setProntoIdsText] = useState("");
  const [syncingPronto, setSyncingPronto] = useState(false);
  const [nextRunMonth, setNextRunMonth] = useState<any>(null);
  const [updatingPB, setUpdatingPB] = useState(false);
  const [showPBModal, setShowPBModal] = useState(false);
  const [duplicates, setDuplicates] = useState<any[]>([]);
  const [scanning, setScanning] = useState(false);
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [cleanupSummary, setCleanupSummary] = useState<any>(null);
  const [stuckOrders, setStuckOrders] = useState<any[]>([]);
  const [loadingStuckOrders, setLoadingStuckOrders] = useState(false);
  const [openRun, setOpenRun] = useState<any>(null);
  const [movingOrderId, setMovingOrderId] = useState<string | number | null>(null);

  const loadStuckOrders = async () => {
    setLoadingStuckOrders(true);
    try {
      const [ordersResponse, runResponse] = await Promise.all([
        getStuckSubscriptionOrders(),
        getOpenSubscriptionRun(),
      ]);
      setStuckOrders(ordersResponse.data?.orders || []);
      setOpenRun((runResponse.data || [])[0] || null);
    } catch (error) {
      console.error("Failed to load stuck subscription orders:", error);
      toast.error("Failed to load stuck subscription orders");
    } finally {
      setLoadingStuckOrders(false);
    }
  };

  const handleMoveOrderToRun = async (order: any) => {
    if (!openRun) {
      toast.error("There is no open subscription run to move this order into");
      return;
    }

    setMovingOrderId(order.orderId);
    try {
      await movePendingOrderToRun(
        order.orderId,
        openRun.id,
        `Admin Tools - move stranded order ${order.orderId} to run ${openRun.id}`
      );
      toast.success(`Order ${order.orderId} moved to ${openRun.name}`);
      await loadStuckOrders();
    } catch (error: any) {
      console.error("Failed to move stuck order:", error);
      toast.error(
        `Failed to move order ${order.orderId}: ${error?.response?.data?.message || error.message}`
      );
    } finally {
      setMovingOrderId(null);
    }
  };

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const config = await fetchConfiguration();
        setDisabledAttributeCodes(Array.isArray(config.disabledAttributeCodes) ? config.disabledAttributeCodes : []);
        setActiveWarehouseCode(typeof config.activeWarehouseCode === "string" ? config.activeWarehouseCode : "");
      } catch (error) {
        console.error('Failed to load configuration:', error);
        toast.error('Failed to load configuration');
      }
    };
    loadConfig();
    loadStuckOrders();
  }, []);

  const handleRemoveCode = (codeToRemove: string) => {
    setDisabledAttributeCodes(prev => prev.filter(code => code !== codeToRemove));
  };

  const handleClearAll = () => {
    setDisabledAttributeCodes([]);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await updateConfiguration({ disabledAttributeCodes, activeWarehouseCode });
      toast.success('Configuration saved successfully');
    } catch (error) {
      console.error('Failed to save configuration:', error);
      toast.error('Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleProntoSync = async () => {
    const raw = prontoIdsText || "";
    const ids = Array.from(
      new Set(
        raw
          .split(/[\s,]+/)
          .map((s) => s.trim())
          .filter((s) => s.length > 0)
      )
    );

    if (!ids.length) {
      toast.error("Please enter at least one Pronto ID");
      return;
    }

    setSyncingPronto(true);
    try {
      await Promise.all(
        ids.map((id) =>
          toast.promise(
            refreshFromPronto(id),
            {
              loading: `Refreshing ${id} from Pronto...`,
              success: () => `Product ${id} queued for refresh`,
              error: (error) => `Error refreshing ${id}: ${error}`,
            }
          )
        )
      );
    } finally {
      setSyncingPronto(false);
    }
  };

  const handleBulkPBUpdate = async () => {
    if (!nextRunMonth) {
      toast.error("Please select a date");
      return;
    }

    // nextRunMonth is likely a dayjs or moment object from DatePicker
    const selectedDate = nextRunMonth.toDate ? nextRunMonth.toDate() : nextRunMonth;
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    
    setUpdatingPB(true);
    try {
      const response = await bulkUpdatePowerboardStartDate(dateStr);
      const { success, failed, total } = response.data;
      toast.success(`Successfully updated ${success}/${total} subscriptions. ${failed} failed.`);
      setShowPBModal(false);
    } catch (error: any) {
      console.error("Bulk PB update failed:", error);
      toast.error(`Bulk update failed: ${error?.response?.data?.message || error.message}`);
    } finally {
      setUpdatingPB(false);
    }
  };

  const handleScanDuplicates = async () => {
    setScanning(true);
    try {
      const response = await findDuplicateDrafts();
      setDuplicates(response.data);
      if (response.data.length === 0) {
        toast.success("No duplicate draft products found!");
      } else {
        toast.success(`Found ${response.data.length} groups of duplicates`);
      }
    } catch (error) {
      console.error("Failed to scan duplicates:", error);
      toast.error("Failed to scan duplicates");
    } finally {
      setScanning(false);
    }
  };

  const prepareCleanup = (group: any) => {
    // Keep the one with the lowest ID (first in the sorted list)
    // Marks the rest for deletion
    const redundant = group.records.slice(1);
    const ids = redundant.map((r: any) => r.id);
    setSelectedProductIds(ids);
    setCleanupSummary({
      sku: group.masterSku,
      toDelete: redundant,
      total: ids.length
    });
    setShowCleanupModal(true);
  };

  const prepareBulkCleanup = () => {
    const allRedundantIds: string[] = [];
    const allRedundantRecords: any[] = [];
    
    duplicates.forEach(group => {
      const redundant = group.records.slice(1);
      allRedundantIds.push(...redundant.map((r: any) => r.id));
      allRedundantRecords.push(...redundant);
    });

    if (allRedundantIds.length === 0) {
      toast.error("No redundant records to cleanup");
      return;
    }

    setSelectedProductIds(allRedundantIds);
    setCleanupSummary({
      sku: "All Groups",
      toDelete: allRedundantRecords,
      total: allRedundantIds.length
    });
    setShowCleanupModal(true);
  };

  const handleCleanup = async () => {
    setCleaningUp(true);
    try {
      const response = await cleanupDuplicateDrafts(selectedProductIds);
      const { success, failed } = response.data.results;
      toast.success(`Cleanup complete: ${success.length} deleted, ${failed.length} failed`);
      setShowCleanupModal(false);
      // Rescan after cleanup
      handleScanDuplicates();
    } catch (error) {
      console.error("Cleanup failed:", error);
      toast.error("Cleanup failed");
    } finally {
      setCleaningUp(false);
    }
  };

  return (
    <div>
      <List title="Admin Tools">
        {openRouterSettings}
        <Card title="CMS Configuration">
          <Space direction="vertical" style={{ width: "100%" }}>
            <div>
              <Text strong>Disabled attribute codes (products with these attribute codes will be hidden from customers):</Text>
              <br />
              <Select
                mode="tags"
                allowClear
                style={{ width: "100%", marginTop: 8, maxWidth: 600 }}
                placeholder="Enter attribute codes to disable (e.g., MEAT, CHEESE, WINE)"
                value={disabledAttributeCodes}
                onChange={setDisabledAttributeCodes}
                tokenSeparators={[',']}
              />
              {disabledAttributeCodes.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Text strong>Currently disabled attribute codes:</Text>
                      <Button size="small" onClick={handleClearAll}>
                        Clear All
                      </Button>
                    </div>
                    <Table
                      dataSource={disabledAttributeCodes.map((code, index) => ({ key: index, code }))}
                      columns={[
                        {
                          title: "Attribute Code",
                          dataIndex: "code",
                          render: (code: string) => <Tag>{code}</Tag>,
                        },
                        {
                          title: "Action",
                          render: (_: any, record: { code: string }) => (
                            <Button size="small" danger onClick={() => handleRemoveCode(record.code)}>
                              Remove
                            </Button>
                          ),
                        },
                      ]}
                      pagination={false}
                      size="small"
                    />
                  </Space>
                </div>
              )}
            </div>

            <div>
              <Text strong>Active warehouse code (used for product availability filter):</Text>
              <Input
                style={{ width: "100%", marginTop: 8, maxWidth: 300 }}
                placeholder="e.g. 311"
                value={activeWarehouseCode}
                onChange={(event) => setActiveWarehouseCode(event.target.value)}
              />
            </div>

            <Space>
              <Button
                type="primary"
                onClick={handleSave}
                loading={loading}
              >
                Save Configuration
              </Button>
            </Space>

            <div style={{ marginTop: 16 }}>
              <Text type="secondary">
                Note: After saving, run a ProntoBridge sync to apply changes to the webclient. You can trigger this manually or wait for the next scheduled sync.
              </Text>
            </div>
          </Space>
        </Card>

        <Card style={{ marginTop: 24 }} title="PowerBoard Bulk Tools">
          <Space direction="vertical" style={{ width: "100%" }}>
            <Text strong>Bulk update subscription start date (PowerBoard only)</Text>
            <Text type="secondary">
              This tool will update the start date of ALL active subscriptions directly in PowerBoard. 
              It will NOT update the local CMS database. This is used for shifting the next payment date for all customers.
            </Text>
            <Space direction="vertical">
              <Text>Select the new start date:</Text>
              <DatePicker 
                value={nextRunMonth} 
                onChange={setNextRunMonth}
                style={{ width: 300 }}
                format="YYYY-MM-DD"
              />
              <Button 
                type="primary" 
                danger 
                onClick={() => setShowPBModal(true)}
                disabled={!nextRunMonth || updatingPB}
              >
                Bulk Update PowerBoard Start Dates
              </Button>
            </Space>
          </Space>
        </Card>

        <Card style={{ marginTop: 24 }} title="Duplicate Draft Cleanup">
          <Space direction="vertical" style={{ width: "100%" }}>
            <Text type="secondary">
              This tool scans the CMS for multiple draft products sharing the same Master SKU. 
              These are often caused by sync failures and can be cleaned up here.
            </Text>
            <Button
              onClick={handleScanDuplicates}
              loading={scanning}
              disabled={scanning}
            >
              Scan for Duplicate Drafts
            </Button>
            
            {duplicates.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <Title level={5}>Found {duplicates.filter(d => d.records.length > 1).length} groups of duplicates</Title>
                <Table
                  dataSource={duplicates}
                  rowKey="masterSku"
                  size="small"
                  columns={[
                    {
                      title: "Master SKU",
                      dataIndex: "masterSku",
                    },
                    {
                      title: "Draft Count",
                      dataIndex: "count",
                      render: (count: number) => <Tag color="orange">{count}</Tag>,
                    },
                    {
                      title: "Actions",
                      render: (_: any, record: any) => (
                        <Button 
                          size="small" 
                          onClick={() => prepareCleanup(record)}
                        >
                          View & Cleanup
                        </Button>
                      ),
                    },
                  ]}
                />
                <div style={{ marginTop: 16 }}>
                  <Button 
                    type="primary" 
                    danger
                    onClick={prepareBulkCleanup}
                  >
                    Bulk Cleanup All Redundant Drafts
                  </Button>
                </div>
              </div>
            )}
          </Space>
        </Card>

        <Card style={{ marginTop: 24 }} title="Stuck Subscription Orders">
          <Space direction="vertical" style={{ width: "100%" }}>
            <Text type="secondary">
              Pending orders left behind in a run that has already closed. These packs will never
              be produced, so move them into the open run. PowerBoard is not changed - the first
              pack was already paid for at signup, so this does not charge the customer again.
            </Text>
            <Space>
              <Button onClick={loadStuckOrders} loading={loadingStuckOrders} disabled={loadingStuckOrders}>
                Scan for Stuck Orders
              </Button>
              {openRun ? (
                <Text>
                  Open run: <b>{openRun.name}</b>
                </Text>
              ) : (
                <Text type="danger">No open run found - orders cannot be moved</Text>
              )}
            </Space>

            {stuckOrders.length > 0 ? (
              <Table
                dataSource={stuckOrders}
                rowKey="orderId"
                size="small"
                pagination={{ pageSize: 10 }}
                columns={[
                  { title: "Order", dataIndex: "orderId" },
                  { title: "Customer", dataIndex: "customerName" },
                  { title: "Subscription", dataIndex: "subscriptionId" },
                  { title: "Stranded Run", dataIndex: "runName" },
                  { title: "Master SKU", dataIndex: "masterSku" },
                  { title: "Executed SKU", dataIndex: "executedSku" },
                  {
                    title: "Created",
                    dataIndex: "createdAt",
                    render: (val: string) => (val ? format(new Date(val), "yyyy-MM-dd HH:mm") : "N/A"),
                  },
                  {
                    title: "Action",
                    render: (_: any, record: any) => (
                      <Button
                        size="small"
                        type="primary"
                        disabled={!openRun}
                        loading={movingOrderId === record.orderId}
                        onClick={() => handleMoveOrderToRun(record)}
                      >
                        Move to {openRun ? openRun.name : "open run"}
                      </Button>
                    ),
                  },
                ]}
              />
            ) : (
              <Text type="secondary">
                {loadingStuckOrders ? "Scanning..." : "No stuck orders found."}
              </Text>
            )}
          </Space>
        </Card>

        <Card style={{ marginTop: 24 }} title="Pronto Sync">
          <Space direction="vertical" style={{ width: "100%" }}>
            <Text strong>Pronto product sync by ID</Text>
            <Text type="secondary">
              Enter one or more Pronto item codes (SKUs), separated by commas or new lines, to queue them for refresh from Pronto into the CMS.
            </Text>
            <textarea
              style={{ width: "100%", minHeight: 120, maxWidth: 600 }}
              placeholder="e.g. 12345, 67890 or one code per line"
              value={prontoIdsText}
              onChange={(e) => setProntoIdsText(e.target.value)}
            />
            <Space>
              <Button
                type="primary"
                onClick={handleProntoSync}
                loading={syncingPronto}
                disabled={syncingPronto}
              >
                Sync from Pronto
              </Button>
            </Space>
          </Space>
        </Card>
      </List>

      <Modal
        title="Confirm Duplicate Cleanup"
        visible={showCleanupModal}
        onOk={handleCleanup}
        onCancel={() => setShowCleanupModal(false)}
        confirmLoading={cleaningUp}
        okText={`Delete ${selectedProductIds.length} Redundant Record(s)`}
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        width={700}
      >
        <p>Are you sure you want to delete the following <strong>redundant</strong> draft records for <strong>{cleanupSummary?.sku}</strong>?</p>
        <Text type="secondary">This will keep the original record and remove the duplicates caused by sync issues.</Text>
        <div style={{ maxHeight: 300, overflowY: 'auto', marginTop: 16 }}>
          <Table
            dataSource={cleanupSummary?.toDelete || []}
            rowKey="id"
            size="small"
            pagination={false}
            columns={[
              { title: "ID", dataIndex: "id", width: 80 },
              { title: "SKU", dataIndex: "masterSku", width: 120 },
              { title: "Name", dataIndex: "name" },
              { title: "Created", dataIndex: "created_at", render: (val) => val ? format(new Date(val), "yyyy-MM-dd HH:mm") : "N/A" },
            ]}
          />
        </div>
        <p style={{ marginTop: 16, color: 'red' }}>This action is irreversible.</p>
      </Modal>

      <Modal
        title="Confirm Bulk PowerBoard Update"
        visible={showPBModal}
        onOk={handleBulkPBUpdate}
        onCancel={() => setShowPBModal(false)}
        confirmLoading={updatingPB}
        okText="Yes, update all active subscriptions"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
      >
        <p>Are you sure you want to update the start date for <strong>ALL</strong> active subscriptions in PowerBoard to <strong>{nextRunMonth ? format(nextRunMonth.toDate ? nextRunMonth.toDate() : nextRunMonth, "do MMMM yyyy") : ""}</strong>?</p>
        <p>This action is irreversible and will directly affect customer payments in PowerBoard.</p>
        <p>Note: This will only affect subscriptions that are currently marked as "active" in the CMS.</p>
      </Modal>
    </div>
  );
};
